import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { getClubEvents, deleteClubEvent } from '../../../api/ClubEventsAPI';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
import { Can } from '../../../hooks/usePermissions';

const ClubEventsPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;

    const [eventsData, setEventsData] = useState({ data: [], per_page: 15, last_page: 0 });
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [triggerFetch, setTriggerFetch] = useState(false);
    const [showToaster, setShowToaster] = useState(false);
    const [toaster, setToaster] = useState({});

    useEffect(() => {
        if (successMessage) {
            setShowToaster(true);
            setToaster({ type: 'success', heading: 'Success!', message: successMessage });
            navigate(location.pathname, { replace: true });
        }
    }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
        setIsLoading(true);
        const filters = { page: currentPage, per_page: perPage, search_term: searchTermCompleted };
        getClubEvents(filters, (response) => {
            setIsLoading(false);
            if (response) {
                setEventsData(response);
            }
        });
    }, [currentPage, perPage, searchTermCompleted, triggerFetch]);

    const handleAddClick = () => navigate('/club_portal/club-events/create');
    const handleEditClick = (id) => navigate(`/club_portal/club-events/${id}/edit`);
    const handleManageMembersClick = (id) => navigate(`/club_portal/club-events/${id}/manage`);

    const handleDeleteClick = (event) => {
        Swal.fire({
            title: 'Are you sure?',
            html: `<p>You are about to delete the event: <strong>${event.name}</strong></p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteClubEvent(event.id, (response) => {
                    if (response.success) {
                        Swal.fire('Deleted!', 'The event has been deleted.', 'success');
                        setTriggerFetch(prev => !prev);
                    } else {
                        Swal.fire('Error!', 'Could not delete the event.', 'error');
                    }
                });
            }
        });
    };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main>
                <div className="container-fluid">
                    <AppBreadCrumb title="Club Events" />
                    <div className="row">
                        <div className="col-12">
                            <div className="tab-wrapper mb-3">
                                <ul className="tabs">
                                    <li className="tab-link active">All Events</li>
                                    <li className="ms-md-auto">
                                        <div className="text-md-end d-flex flex-column flex-md-row align-items-md-center p-2">
                                            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
                                              <Can any={['create-club-event']}>
                                            <button className="text-nowrap btn btn-primary btn-sm mt-2 mt-md-0 ms-md-3 fw-semibold f-s-14" type="button" onClick={handleAddClick}>
                                                <i className="fas fa-plus f-s-18"></i> Add Event
                                            </button>
                                            </Can>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="content-wrapper">
                                <div className="d-flex m-md-3">
                                    {eventsData.data.length > 0 && (
                                        <>
                                            <AppPaginator entityName="Events" currentPage={currentPage} totalPages={eventsData.last_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    )}
                                    {isLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>
                                <div className="tabs-content active">
                                    <div className="row">
                                        {!isLoading && (
                                            eventsData.data.length > 0 ? eventsData.data.map(event => (
                                                <div className="col-md-6 col-xl-4 project-card" key={event.id}>
                                                    <div className="card hover-effect">
                                                        <div className="card-header">
                                                            <div className="d-flex align-items-center">
                                                                <div className="h-40 w-40 d-flex-center b-r-50 overflow-hidden bg-light-primary text-primary">
                                                                    <i className="fas fa-calendar-alt fa-lg"></i>
                                                                </div>
                                                                <div className="flex-grow-1 ps-2">
                                                                    <h6 className="m-0 text-dark f-w-600">{event.name}</h6>
                                                                </div>
                                                                  <Can any={['manage-event-members','edit-club-event','delete-club-event']}>
                                                                <div className="dropdown">
                                                                    <button aria-expanded="false" className="bg-none border-0" data-bs-toggle="dropdown" type="button">
                                                                        <i className="ti ti-dots-vertical text-dark" />
                                                                    </button>
                                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                                          <Can any={['manage-event-members']}>
                                                                        <li><a className="dropdown-item" onClick={() => handleManageMembersClick(event.id)}><i className="fas fa-users text-info" /> Manage Members</a></li>
                                                                        </Can>
                                                                        <Can any={['edit-club-event']}>
                                                                        <li><a className="dropdown-item" onClick={() => handleEditClick(event.id)}><i className="ti ti-edit text-success" /> Edit</a></li>
                                                                        </Can>
                                                                        <Can any={['delete-club-event']}>
                                                                        <li><a className="dropdown-item delete-button" onClick={() => handleDeleteClick(event)}><i className="ti ti-trash text-danger" /> Delete</a></li>
                                                                        </Can>
                                                                    </ul>
                                                                </div>
                                                                </Can>
                                                            </div>
                                                        </div>
                                                        <div className="card-body">
                                                            <p className="text-muted f-s-14">{event.description?.substring(0, 100) || 'No description'}</p>
                                                            <hr/>
                                                            <div className="d-flex justify-content-between">
                                                                <div>
                                                                    <h6 className="text-dark f-s-14">Starts: <span className="text-primary">{new Date(event.start_time).toLocaleString()}</span></h6>
                                                                </div>
                                                                <div className="text-end">
                                                                    <h6 className="text-dark f-s-14">Location: <span className="text-primary">{event.location || 'N/A'}</span></h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )) : (
                                                <div className="card mb-3 hover-effect">
                                                    <div className="row g-0">
                                                        <div className="col-md-6 col-xl-8"><div className="card-body"><h5 className="card-title">No Events Found</h5><p className="card-text">No records found. Click "Add Event" to get started.</p></div></div>
                                                        <div className="col-md-6 col-xl-4"><img alt="No data illustration" className="img-fluid" src="/assets/images/blog-app/09.jpg" /></div>
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default ClubEventsPage;